import { useMemo } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { formatAccruedAmount } from "@common/helpers/domains/accounting/accruedBillingRule";
import { SCHEDULE_NOTHING_TO_BILL_MESSAGE } from "@common/helpers/domains/accounting/scheduleBillingEligibility";
import { fromMonetaryToInt } from "@common/helpers/monetary";
import { ScheduleType } from "@common/types/domains/accounting/schedule";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { useLockDate } from "@api/lock.api";
import { useMarkScheduleBilledMutation } from "@api/scheduleBilling.api";
import { getApiErrorMessage } from "@api/apiError";
import { validateFormikWithZodSchema } from "@helpers/form";
import { getFirstUnlockedDateOnly } from "@helpers/lock";
import { ScheduleAmountInput } from "../ScheduleAmountInput";
import {
    ScheduleBillingModalFooter,
    ScheduleBillingModalShell,
} from "./ScheduleBillingModalShell";
import {
    buildMarkBilledSuccessToast,
    buildRecognitionAccountHelperText,
} from "./scheduleBillingCopy";
import {
    ScheduleBillingTarget,
    getDefaultBilledDate,
    getUnbilledBalance,
} from "./scheduleBillingRowHelpers";

interface MarkBilledValues {
    billedDate: string;
    amount: number;
}

const fields = createFormikFields<MarkBilledValues>();

function buildMarkBilledSchema(unbilledBalance: number) {
    return z.object({
        billedDate: z.string().date("Pick a billed date"),
        amount: z
            .number()
            .positive("Enter an amount above zero")
            .refine(
                (amount) =>
                    fromMonetaryToInt(amount) <=
                    fromMonetaryToInt(unbilledBalance),
                {
                    message: `Enter at most ${formatAccruedAmount(unbilledBalance)}, the unbilled balance on this date`,
                },
            ),
    });
}

function buildAmountDescription(options: {
    hasNothingToBill: boolean;
    recognitionAccountName: string | null;
    scheduleType: ScheduleType;
}): string | undefined {
    if (options.hasNothingToBill) {
        return SCHEDULE_NOTHING_TO_BILL_MESSAGE;
    }
    return options.recognitionAccountName
        ? buildRecognitionAccountHelperText({
              recognitionAccountName: options.recognitionAccountName,
              scheduleType: options.scheduleType,
          })
        : undefined;
}

const ScheduleMarkBilledForm = ({
    target,
    onHide,
}: {
    target: ScheduleBillingTarget;
    onHide: () => void;
}) => {
    const { toast, error } = useToaster();
    const lockDate = useLockDate(target.entityId);
    const firstUnlockedDate = getFirstUnlockedDateOnly(lockDate);
    const mutation = useMarkScheduleBilledMutation();
    const initialValues = useMemo(() => {
        const billedDate = getDefaultBilledDate(firstUnlockedDate);
        return {
            billedDate,
            amount: getUnbilledBalance(target.billing, billedDate),
        };
    }, [firstUnlockedDate, target.billing]);

    const formik = useFormik<MarkBilledValues>({
        initialValues,
        enableReinitialize: true,
        validate: (values) => {
            const unbilledBalance = getUnbilledBalance(
                target.billing,
                values.billedDate,
            );
            if (unbilledBalance === 0) {
                return { amount: SCHEDULE_NOTHING_TO_BILL_MESSAGE };
            }
            return validateFormikWithZodSchema(
                buildMarkBilledSchema(unbilledBalance),
            )(values);
        },
        onSubmit: async (values) => {
            const unbilledBalance = getUnbilledBalance(
                target.billing,
                values.billedDate,
            );
            try {
                await mutation.mutateAsync({
                    applicationId: target.applicationId,
                    entityId: target.entityId,
                    billedDate: values.billedDate,
                    amount: values.amount,
                });
                toast(
                    buildMarkBilledSuccessToast({
                        amount: values.amount,
                        unbilledBalance,
                        scheduleType: target.billing.scheduleType,
                    }),
                    undefined,
                    { variant: "success" },
                );
                onHide();
            } catch (mutationError) {
                error(
                    getApiErrorMessage(
                        mutationError,
                        "Failed to mark the schedule as billed",
                    ),
                );
            }
        },
    });

    const hasNothingToBill =
        getUnbilledBalance(target.billing, formik.values.billedDate) === 0;

    return (
        <FormikProvider value={formik}>
            <Form>
                <VStack gap={16}>
                    <fields.Date
                        name="billedDate"
                        label="Billed date"
                        minDate={firstUnlockedDate}
                        onValueChange={(billedDate) => {
                            void formik.setFieldValue(
                                "amount",
                                getUnbilledBalance(target.billing, billedDate),
                            );
                        }}
                    />
                    <FormikField<number>
                        name="amount"
                        label="Amount"
                        description={buildAmountDescription({
                            hasNothingToBill,
                            recognitionAccountName:
                                target.billing.recognitionAccountName,
                            scheduleType: target.billing.scheduleType,
                        })}
                    >
                        {({ field, helpers, invalid }) => (
                            <ScheduleAmountInput
                                name={field.name}
                                value={field.value}
                                onValueChange={(amount) =>
                                    void helpers.setValue(amount)
                                }
                                onBlur={() => void helpers.setTouched(true)}
                                isInvalid={invalid}
                            />
                        )}
                    </FormikField>
                    <ScheduleBillingModalFooter
                        isSubmitting={formik.isSubmitting}
                        isDisabled={hasNothingToBill}
                        onCancel={onHide}
                    />
                </VStack>
            </Form>
        </FormikProvider>
    );
};

interface ScheduleMarkBilledModalProps {
    target: ScheduleBillingTarget | null;
    onHide: () => void;
}

export const ScheduleMarkBilledModal = ({
    target,
    onHide,
}: ScheduleMarkBilledModalProps) => (
    <ScheduleBillingModalShell target={target} onHide={onHide}>
        {(displayedTarget) => (
            <ScheduleMarkBilledForm target={displayedTarget} onHide={onHide} />
        )}
    </ScheduleBillingModalShell>
);
